'use client';

import { c as _c } from "react/compiler-runtime";
import { type Post } from '@shared/all/domains/posts';
import { getCaretCoordinates } from '@shared/utils/helpers/getCaretCoordinates';
import toast from '@shared/utils/helpers/toast';
import usePostsShared from '@shared/utils/hooks/usePosts';
import usePreference from '@shared/utils/hooks/usePreference';
import useTagSuggestion from '@shared/utils/hooks/useTagSuggestion';
import useUser from '@shared/utils/hooks/useUser';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import useContentUIRenderer from '../../hooks/useContentUIRenderer';
import { client } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
import TagSuggestionModal from './TagSuggestionModal';
type Props = {
  closeHandler: () => void;
  childPost: Post;
  isChildPostPolitics?: boolean;
  isChildPostNsfw?: boolean;
};
const NewRepostModal = props => {
  const $ = _c(74);
  const {
    currentUser
  } = useUser();
  const {
    contentUIRenderHandler
  } = useContentUIRenderer();
  const {
    preferenceState
  } = usePreference(client);
  const {
    appendPost
  } = usePostsShared(client);
  const [content, setContent] = useState("");
  const [buttonLoading, setButtonLoading] = useState(false);
  const textareaRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = async () => {
      const textarea = textareaRef.current;
      if (!textarea) {
        return {
          top: 0,
          left: 0
        };
      }
      const coords = getCaretCoordinates(textarea);
      return {
        top: coords.top + coords.height,
        left: coords.left
      };
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== content) {
    t1 = {
      content,
      setContent,
      inputRef: textareaRef,
      TagSuggestionModal,
      getCaretPosition: t0
    };
    $[1] = content;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    renderSuggestions
  } = useTagSuggestion(t1);
  let t2;
  if ($[3] !== appendPost || $[4] !== content || $[5] !== props) {
    t2 = async () => {
      setButtonLoading(true);
      if (props.childPost.repost?.id) {
        return toast("You cannot repost a repost", "", "error");
      }
      const [, data] = await client.posts.new.mutate({
        content,
        repostId: props.childPost.id,
        uploads: [],
        addFactiiis: []
      });
      if (data) {
        appendPost(data.post);
      }
      props.closeHandler();
      setButtonLoading(false);
    };
    $[3] = appendPost;
    $[4] = content;
    $[5] = props;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const submitHandler = t2;
  let t3;
  if ($[7] !== content.length) {
    t3 = () => {
      if (content.length > 5000) {
        setContent(_temp);
      }
    };
    $[7] = content.length;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== content) {
    t4 = [content];
    $[9] = content;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] !== contentUIRenderHandler || $[12] !== props.childPost.content || $[13] !== props.childPost.id[0]) {
    t5 = contentUIRenderHandler(props.childPost.content, props.childPost.id[0], false);
    $[11] = contentUIRenderHandler;
    $[12] = props.childPost.content;
    $[13] = props.childPost.id[0];
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const renderedContent = t5;
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <h2>Repost</h2>;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== props.closeHandler) {
    t7 = <ModalHeader closeHandler={props.closeHandler}>{t6}</ModalHeader>;
    $[16] = props.closeHandler;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = currentUser.avatarKey ?? null;
  const t9 = currentUser.robohash ?? "";
  const t10 = currentUser.tag === "BOT";
  let t11;
  if ($[18] !== currentUser.id || $[19] !== t10 || $[20] !== t8 || $[21] !== t9) {
    t11 = <Avatar avatarKey={t8} robohash={t9} isBot={t10} size={35} userId={currentUser.id} />;
    $[18] = currentUser.id;
    $[19] = t10;
    $[20] = t8;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = e => setContent(e.target.value);
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== submitHandler) {
    t13 = event => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        submitHandler();
      }
    };
    $[24] = submitHandler;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== content || $[27] !== t13) {
    t14 = <textarea ref={textareaRef} autoFocus={true} rows={4} placeholder="Join the conversation..." value={content} onChange={t12} onKeyDown={t13} className="flex-1 rounded border border-ds-border-standard p-2 focus:outline-none focus:ring-0" />;
    $[26] = content;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t11 || $[30] !== t14) {
    t15 = <div className="flex gap-1.5">{t11}{t14}</div>;
    $[29] = t11;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== content.length) {
    t16 = content.length > 300 && <p className="text-start text-yellow-600">We suggest keeping your content under 300 characters. ({content.length.toLocaleString()}/300)</p>;
    $[32] = content.length;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== content.length) {
    t17 = content.length > 4900 && <p className="text-start text-destructive">Content must be below 5,000 characters. ({content.length.toLocaleString()}/5,000)</p>;
    $[34] = content.length;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const t18 = props.childPost.user.avatarKey ?? null;
  const t19 = props.childPost.user.robohash ?? "";
  const t20 = props.childPost.user.tag === "BOT";
  let t21;
  if ($[36] !== props.childPost.user.anonymous || $[37] !== props.childPost.user.id || $[38] !== t18 || $[39] !== t19 || $[40] !== t20) {
    t21 = <Avatar avatarKey={t18} robohash={t19} isBot={t20} size={35} userId={props.childPost.user.id} isAnonymous={props.childPost.user.anonymous} />;
    $[36] = props.childPost.user.anonymous;
    $[37] = props.childPost.user.id;
    $[38] = t18;
    $[39] = t19;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== props.childPost.user.name || $[43] !== props.childPost.user.username) {
    t22 = <div className="flex items-center space-x-2">{props.childPost.user.name ? <><p className="font-semibold">{props.childPost.user.name}</p><span className="text-textSecondary/70">@{props.childPost.user.username}</span></> : <p className="font-semibold">@{props.childPost.user.username}</p>}</div>;
    $[42] = props.childPost.user.name;
    $[43] = props.childPost.user.username;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== preferenceState.allowPoliticalContent || $[46] !== props.isChildPostPolitics || $[47] !== renderedContent) {
    t23 = !preferenceState.allowPoliticalContent && props.isChildPostPolitics ? <div className="rounded-md bg-background-secondary p-4 text-start shadow-md"><p className="mb-2 text-lg font-semibold text-destructive">Content Marked Political</p><p className="text-textPrimary">Please update your settings by clicking <Link onClick={_temp2} href="/settings/preferences"><span className="text-primary underline hover:text-indigo-400">here</span></Link>.</p></div> : renderedContent;
    $[45] = preferenceState.allowPoliticalContent;
    $[46] = props.isChildPostPolitics;
    $[47] = renderedContent;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== t22 || $[50] !== t23) {
    t24 = <div>{t22}{t23}</div>;
    $[49] = t22;
    $[50] = t23;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t21 || $[53] !== t24) {
    t25 = <div className="flex items-start gap-2 rounded-md border border-ds-border-standard bg-background-secondary p-2 shadow-md">{t21}{t24}</div>;
    $[52] = t21;
    $[53] = t24;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== t15 || $[56] !== t16 || $[57] !== t17 || $[58] !== t25) {
    t26 = <ModalContent>{t15}{t16}{t17}{t25}</ModalContent>;
    $[55] = t15;
    $[56] = t16;
    $[57] = t17;
    $[58] = t25;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  const t27 = content.length === 0 || buttonLoading;
  let t28;
  if ($[60] !== buttonLoading || $[61] !== submitHandler || $[62] !== t27) {
    t28 = <ModalFooter><Button text="Repost" onClick={submitHandler} disabled={t27} loading={buttonLoading} classNames="w-28" /></ModalFooter>;
    $[60] = buttonLoading;
    $[61] = submitHandler;
    $[62] = t27;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== props.closeHandler || $[65] !== t26 || $[66] !== t28 || $[67] !== t7) {
    t29 = <Modal closeHandler={props.closeHandler}>{t7}{t26}{t28}</Modal>;
    $[64] = props.closeHandler;
    $[65] = t26;
    $[66] = t28;
    $[67] = t7;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== renderSuggestions) {
    t30 = renderSuggestions();
    $[69] = renderSuggestions;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== t29 || $[72] !== t30) {
    t31 = <>{t29}{t30}</>;
    $[71] = t29;
    $[72] = t30;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  return t31;
};
export default NewRepostModal;
function _temp(content_0) {
  return content_0.slice(0, 5000);
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}